<template>
	<div class="modal-overlay" @click="handleOverlayClick">
		<div class="modal-content">
			<div class="modal-header">
				<h3>View Credit Report</h3>
				<span class="close" @click="onClose">&times;</span>
			</div>

			<div class="modal-controls">
				<div class="button-group">
					<button @click="selectBorrowerTypeId(1)" :class="borrowerButtonClass(1)">Primary Borrower</button>
					<button @click="selectBorrowerTypeId(2)" :class="borrowerButtonClass(2)">Co-Borrower</button>
				</div>
				<select v-model="selectedCreditReportId" v-if="creditReportOptions.length > 0">
					<option v-for="report in creditReportOptions" :key="report.id" :value="report.id">
						{{ report.name }}
					</option>
				</select>
			</div>

			<div class="modal-body">
				<div class="modal-main">
					<template v-if="isLoading">
						<div class="container">
							<Spinner />
						</div>
					</template>
					<template v-else-if="selectedCreditReportId">
						<textarea readonly v-model="rawCreditReport"></textarea>
					</template>
					<template v-else>
						<div class="container">
							<DocumentMagnifyingGlassIcon class="empty-icon" />
							<p>{{ emptyStateMessage }}</p>
						</div>
					</template>
				</div>
			</div>

			<div class="modal-footer">
				<button @click="onClose">Close</button>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { onMounted, onUnmounted, ref, computed, watchEffect } from 'vue';
	import axios from 'axios';
	import functions from '@/use/functions';
	import Spinner from '@/components/shared/Spinner.vue';
	import { DocumentMagnifyingGlassIcon } from '@heroicons/vue/24/solid';
	import { BorrowerCreditReportModel } from '@/models/loans';

	const props = defineProps<{
		annualReviewId: number | undefined;
		creditPullTypeId: number;
		onClose: () => void;
	}>();

	const isLoading = ref<boolean>(false);
	const creditReports = ref<BorrowerCreditReportModel[]>();
	const selectedBorrowerTypeId = ref<number>(1);
	const selectedCreditReportId = ref<number | null>();
	const rawCreditReport = ref<string>('');

	const handleOverlayClick = (event: MouseEvent) => {
		const target = event.target as HTMLElement;

		if (target.classList.contains('modal-overlay')) {
			props.onClose();
		}
	};

	const selectBorrowerTypeId = (borrowerTypeId: number) => (selectedBorrowerTypeId.value = borrowerTypeId);

	const borrowerButtonClass = (borrowerTypeId: number) =>
		selectedBorrowerTypeId.value === borrowerTypeId ? '' : 'secondary';

	const creditReportOptions = computed(() => {
		if (!creditReports.value) {
			return [];
		}

		return creditReports.value.filter((r) => r.borrowerTypeId === selectedBorrowerTypeId.value);
	});

	const emptyStateMessage = computed(() =>
		creditReportOptions.value.length > 0 ? 'No credit report selected' : 'No credit reports found'
	);

	const fetchCreditReportsAsync = async () => {
		isLoading.value = true;

		try {
			const queryParams = {
				creditPullTypeId: props.creditPullTypeId
			};

			const response = await axios.get<BorrowerCreditReportModel[]>(
				`/api/annualreview/${props.annualReviewId}/credit-reports`,
				{ params: queryParams }
			);

			creditReports.value = response.data;
		} catch (error) {
			functions.openModal({
				title: 'Oops!',
				description: `There was an issue getting the credit reports. Please try again later.`
			});
		} finally {
			isLoading.value = false;
		}
	};

	const fetchRawCreditReportAsync = async (creditReportId: number) => {
		if (!creditReportId || !creditReports.value) {
			return;
		}

		const rawCreditReportId = creditReports.value.find((r) => r.id === creditReportId)?.rawCreditReportId;

		isLoading.value = true;

		try {
			const response = await axios(`/api/annualreview/${creditReportId}/raw-credit-report/${rawCreditReportId}`);

			rawCreditReport.value = response.data;
		} catch (error) {
			functions.openModal({
				title: 'Oops!',
				description: `There was an issue getting the raw credit report. Please try again later.`
			});

			rawCreditReport.value = '';
		} finally {
			isLoading.value = false;
		}
	};

	watchEffect(() => {
		const invalidReportId = !creditReportOptions.value.some((report) => report.id === selectedCreditReportId.value);

		if (!selectedCreditReportId.value || invalidReportId) {
			selectedCreditReportId.value = creditReportOptions.value[0]?.id ?? null;
		}

		if (selectedCreditReportId.value) {
			fetchRawCreditReportAsync(selectedCreditReportId.value);
		}
	});

	onMounted(async () => {
		document.body.style.overflow = 'hidden';

		await fetchCreditReportsAsync();
	});

	onUnmounted(() => {
		document.body.style.overflow = '';
	});
</script>

<style scoped>
	.modal-overlay {
		position: fixed;
		top: 0;
		left: 0;
		width: 100vw;
		height: 100vh;
		background-color: rgba(0, 0, 0, 0.5);
		display: flex;
		justify-content: center;
		align-items: center;
		z-index: 1000000; /* stupid navbar... */
		cursor: default;
	}

	.modal-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 1rem;
	}

	.modal-content {
		position: relative;
		background: white;
		padding: 20px;
		border-radius: 8px;
		width: 50vw;
		height: 90vh;
		box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
		display: flex;
		flex-direction: column;
	}

	.modal-controls {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 10px;
		margin-bottom: 1rem;
		flex-wrap: wrap;
	}

	select {
		max-width: fit-content;
		height: auto;
		padding: 5px 10px;
	}

	.button-group {
		display: flex;
		gap: 10px;
	}

	.modal-body {
		flex-grow: 1;
		display: flex;
		flex-direction: column;
	}

	.modal-main {
		flex-grow: 1;
		width: 100%;
		resize: none;
		overflow-x: hidden;
		overflow-y: hidden;
	}

	.modal-footer {
		display: flex;
		justify-content: flex-end;
		padding-top: 1rem;
	}

	textarea {
		font-family: monospace;
		white-space: pre;
		overflow-x: auto;
		overflow-y: auto;
		color: #888;
		background-color: #fafafa;
		border-radius: 4px;
		width: 100%;
		height: 100%;
		resize: none;
		padding: 1rem;
		text-align: center;
	}

	.close {
		font-size: 32px;
		cursor: pointer;
		transition: color 0.3s;
	}

	.close:hover {
		color: #888;
	}

	button {
		padding: 10px 14px;
		background-color: black;
		color: white;
		border: none;
		border-radius: 4px;
		cursor: pointer;
		font-size: 13px;
		font-family: 'Montserrat', arial, sans-serif;
		font-weight: bold;
		transition: background-color 0.2s, color 0.2s, opacity 0.2s;
	}

	button.secondary {
		box-shadow: none;
		background-color: #ddd;
		color: black;
	}

	button:hover {
		background-color: #333;
	}

	button.secondary:hover {
		background-color: black;
		color: white;
	}

	.spinner-container {
		display: flex;
		align-items: center;
		justify-content: center;
		height: 100%;
	}

	.container {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		height: 100%;
		color: #888;
		background-color: #fafafa;
		border: 2px solid #ddd;
		border-radius: 4px;
		text-align: center;
	}

	.container p {
		font-size: 24px;
	}

	.empty-icon {
		width: 80px;
		height: 80px;
		color: #bbb;
	}

	@media (max-width: 1024px) {
		.modal-content {
			width: 100%;
			height: 100%;
			border-radius: 0;
		}

		.modal-body {
			flex-direction: column;
			overflow-y: auto;
		}
	}

	@media (max-width: 768px) {
		.modal-controls {
			flex-direction: column;
			align-items: stretch;
		}

		.button-group {
			width: 100%;
			display: flex;
			flex-direction: column;
			justify-content: center;
		}

		button,
		select {
			min-width: 100%;
		}
	}
</style>
